import axios from "axios"
import { GetGradesProps, ApiResponse, GetSubmissionsProps,
    GetSubmissionsData, Submission, GetSubmissionProps,
    PostSubmissionsData, PostSubmissionsProps, GetGradesData,
    GetSubmissionDrawingQuestionsProps,
    GetSubmissionDrawingQuestionsResponse} from "../types/assignmentTypes"
import { StringBoolean } from "shared/types/commonTypes"

export async function getGrades({ assignmentId }: GetGradesProps): Promise<ApiResponse<GetGradesData>> {
    const response = await axios.get(`/api/assignments/${assignmentId}/grades`)
    return response.data
}

export async function getSubmissions({ assignmentId }: GetSubmissionsProps): Promise<ApiResponse<GetSubmissionsData>> {
    const response = await axios.get(`/api/assignments/${assignmentId}/submissions`)
    return response.data
}

export async function getSubmission({ submissionId }: GetSubmissionProps): Promise<ApiResponse<Submission>> {
    const response = await axios.get(`/api/submissions/${submissionId}`)
    return response.data
}

export type AssessmentQuestion = {
    id: string
    type: string
    number: number
    question: string
    correctAnswer: string
    explanation?: string
    isDrawing: StringBoolean
    randomizable: boolean
    answers: {
        letter: string
        originalLetter: string
        answer: string
        explanation?: string
        imageExists: boolean
        image: {
            letter: string
            update: string
            altText: string
        }
    }[]
    image: {
        number: number
        update: string
        altText: string
    }
    imageExists: boolean
    contextId?: string
}

export type AssessmentQuestionContext = {
    assessmentId: string
    contextId: string
    text: string
}

export async function postSubmissions({ assignmentId, status }: PostSubmissionsProps): Promise<ApiResponse<PostSubmissionsData>> {
    const response = await axios.post(`/api/assignments/${assignmentId}/submissions`, { status })
    return response.data
}

export async function getAssessmentQuestions({ assessmentId, randomizable }: { assessmentId: string; randomizable: boolean }): Promise<AssessmentQuestion[]> {
    const response: ApiResponse<AssessmentQuestion[]> = await axios.get(`/api/assessments/${assessmentId}/questions`, { params: { randomizable } })
    return response.data
}

interface UpdateAssessmentQuestionProps {
    assessmentId: string
    questionNumber: number
    answers?: string // JSON string of answers array
    answersImageAltTexts?: string // JSON string of answers' alt texts array
    questionText?: string
    explanation?: string
    isDrawing?: StringBoolean
    randomizable?: boolean
    position?: number
    contextId?: string
    subtopicIds?: string // JSON string of subtopicIds array
}

export async function updateAssessmentQuestion(values: UpdateAssessmentQuestionProps): Promise<string> {
    const formData = new FormData()
    for (const [key, val] of Object.entries(values)) {
        formData.append(key, val)
    }
    const response: ApiResponse<string> = await axios.put(`/api/assessments/${values.assessmentId}/question`, formData)
    return response.data
}

export async function deleteAssessmentQuestion({assessmentId, ...body}): Promise<string> {
    const response: ApiResponse<string> = await axios.delete(`/api/assessments/${assessmentId}/question`, { data: body })
    return response.data
}

export async function getSubmissionDrawingQuestions({ submissionId }: GetSubmissionDrawingQuestionsProps): Promise<GetSubmissionDrawingQuestionsResponse> {
    const response = await axios.get(`/api/submissions/${submissionId}/drawing-questions`)
    return response.data
}

export async function getAssessmentQuestionContexts({ assessmentId }: { assessmentId: string }): Promise<AssessmentQuestionContext[]> {
    const response: ApiResponse<AssessmentQuestionContext[]> = await axios.get(`/api/assessments/${assessmentId}/contexts`)
    return response.data
}

export async function updateAssessmentQuestionContext({ assessmentId, contextId, text }): Promise<string> {
    const response: ApiResponse<string> = await axios.put("/api/editing/assessment/question/context", { assessmentId, contextId, text })
    return response.data
}

export async function deleteAssessmentQuestionContext({ contextId }): Promise<string> {
    const response: ApiResponse<string> = await axios.delete("/api/editing/assessment/question/context", { data: { contextId } })
    return response.data
}


const assignments = {
    getGrades,
    getSubmissions,
    postSubmissions,
    getSubmission,
    getAssessmentQuestions,
    updateAssessmentQuestion,
    deleteAssessmentQuestion,
    getAssessmentQuestionContexts,
    updateAssessmentQuestionContext,
    deleteAssessmentQuestionContext
}

export default assignments
